import React from 'react'

import { FiWifi, FiWifiOff } from 'react-icons/fi'

import { useApi, useAuth } from '@/hooks'
import { type DataSource } from '@/types/data-source'

/** The insight's date selection, as its `date` / `from` / `to` parameters. */
export interface AssetDateRange {
  date?: string
  from?: string
  to?: string
}

interface AssetCurrentStatus {
  id: number
  gatewayOn: boolean
  hasData: boolean
}

/**
 * Gateway status for every asset in the picker, from GET /fresh/assets/current-status.
 *
 * Gives each option the asset map's online/offline wifi icon, and backs the support-only
 * "Has current data" filter, which narrows the list to assets whose unit has reported since
 * the start of the insight's date range. Staff use it when testing an insight, to skip the
 * hundreds of assets that would only show "no data".
 *
 * Fresh only: the status is keyed by Fresh asset ids, which a Timescale list doesn't use.
 */
export const useAssetCurrentStatus = (companyId?: number, dateRange?: AssetDateRange, dataSource?: DataSource) => {
  const { api } = useApi()
  const { hasPermission } = useAuth()
  const isStaff = hasPermission('staff')
  const [statusById, setStatusById] = React.useState<Map<string, AssetCurrentStatus>>()
  const [hasDataOnly, setHasDataOnly] = React.useState(false)
  const { date, from, to } = dateRange ?? {}
  const enabled = !!companyId && (!dataSource || dataSource === 'fresh')

  React.useEffect(() => {
    setStatusById(undefined)
    if (!api || !enabled) {
      return
    }
    let cancelled = false
    const query = new URLSearchParams({ companyId: String(companyId) })
    if (date) query.set('date', date)
    if (date === 'custom' && from) query.set('from', from)
    if (date === 'custom' && to) query.set('to', to)
    void api
      .simpleGet<{ data: AssetCurrentStatus[] }>({
        path: `/fresh/assets/current-status?${query.toString()}`,
        origin: 'AssetDropdown#status',
        expiresIn: '5m',
      })
      .then((response) => {
        if (!cancelled) {
          setStatusById(new Map(response.data.map((s) => [String(s.id), s])))
        }
      })
      // The icons and the filter are extras; without them the picker works as before.
      .catch((error: unknown) => console.error(error))
    return () => {
      cancelled = true
    }
  }, [api, enabled, companyId, date, from, to])

  // One element per state, shared by every option, rather than thousands of identical ones.
  const icons = React.useMemo(
    () => ({
      online: <FiWifi className="w-4 h-4 text-blue-500 shrink-0" aria-label="Gateway online" />,
      offline: <FiWifiOff className="w-4 h-4 text-gray-500 shrink-0" aria-label="Gateway offline" />,
    }),
    []
  )

  /** The wifi icon for an asset, or nothing when its status isn't known. */
  const iconFor = React.useCallback(
    (assetId: string): React.ReactNode => {
      const status = statusById?.get(assetId)
      if (!status) return undefined
      return status.gatewayOn ? icons.online : icons.offline
    },
    [statusById, icons]
  )

  /** Whether an asset survives the filter. Selected assets always stay, so a choice isn't lost. */
  const isShown = React.useCallback(
    (assetId: string, selected: boolean): boolean =>
      !hasDataOnly || !statusById || selected || statusById.get(assetId)?.hasData === true,
    [hasDataOnly, statusById]
  )

  /**
   * The support-only switch, given the ids of every asset in the list. While it is on it
   * reports how many of them have current data, e.g. "100 of 300".
   */
  const toggle = (assetIds: string[]): React.ReactNode => {
    if (!isStaff || !enabled) return null
    const withData = statusById ? assetIds.filter((id) => statusById.get(id)?.hasData).length : 0
    return (
      <label className="label cursor-pointer justify-start gap-2 mt-1 py-0 text-sm">
        <input
          type="checkbox"
          className="toggle toggle-xs toggle-primary"
          checked={hasDataOnly}
          disabled={!statusById}
          onChange={() => setHasDataOnly(!hasDataOnly)}
        />
        Has current data (support only)
        {hasDataOnly && statusById && (
          <span className="text-base-content/60">
            {withData} of {assetIds.length}
          </span>
        )}
      </label>
    )
  }

  return { iconFor, isShown, toggle }
}
